import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AED, AED_short } from '../lib/api';
import { motion } from 'framer-motion';
import {
  LogOut, Receipt, AlertTriangle, Building2, FileSpreadsheet, KeyRound, Loader2,
  Users, UserPlus, Copy, ShieldCheck, Shield, MoreVertical, Trash2, X, Check, RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL;
const V_TOKEN_KEY = 'iris_vendor_token';
const V_WS_KEY = 'iris_vendor_workspace';

// Standalone axios instance so vendor sessions don't clash with the main app auth
const vapi = axios.create({ baseURL: `${BASE}/api` });
vapi.interceptors.request.use(cfg => {
  const t = localStorage.getItem(V_TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Workspace persisted from login (defaults to 'demo' for the demo build)
  const ws = localStorage.getItem(V_WS_KEY) || localStorage.getItem('flipn_workspace') || 'demo';
  cfg.headers['X-Workspace'] = ws;
  return cfg;
});

export default function VendorPortal() {
  const [phase, setPhase] = useState('login'); // login | change-password | dashboard
  const [me, setMe] = useState(null);
  const [data, setData] = useState({ invoices: [], boqs: [], snags: [], properties: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('overview');

  // On mount, try to hydrate an existing session
  useEffect(() => {
    const t = localStorage.getItem(V_TOKEN_KEY);
    if (!t) return;
    loadMe().catch(() => { localStorage.removeItem(V_TOKEN_KEY); });
  }, []);

  const loadMe = async () => {
    setLoading(true);
    try {
      const r = await vapi.get('/vendor-portal/me');
      setMe(r.data);
      if (r.data?.user?.must_change_password) {
        setPhase('change-password');
      } else {
        setPhase('dashboard');
        await loadAll();
      }
    } finally { setLoading(false); }
  };

  const loadAll = async () => {
    const perms = me?.user?.vendor_permissions || {};
    const canBoq = perms.view_boqs !== false;
    const canInv = perms.view_invoices !== false;
    const canSnag = perms.view_snags !== false;
    const [inv, boq, sn, pr] = await Promise.all([
      canInv ? vapi.get('/vendor-portal/invoices').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      canBoq ? vapi.get('/vendor-portal/boqs').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      canSnag ? vapi.get('/vendor-portal/snags').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      vapi.get('/vendor-portal/properties').catch(() => ({ data: [] })),
    ]);
    setData({ invoices: inv.data, boqs: boq.data, snags: sn.data, properties: pr.data });
  };

  const logout = () => {
    localStorage.removeItem(V_TOKEN_KEY);
    localStorage.removeItem(V_WS_KEY);
    setMe(null);
    setPhase('login');
  };

  return (
    <div className="min-h-screen" data-testid="vendor-portal">
      <div className="ambient-bg"/>

      {phase === 'login' && <LoginForm onDone={loadMe}/>}
      {phase === 'change-password' && <ChangePasswordForm user={me?.user} onDone={async () => { setPhase('dashboard'); await loadAll(); }}/>}
      {phase === 'dashboard' && me && (
        <VendorDashboard
          me={me}
          data={data}
          tab={tab}
          setTab={setTab}
          onReload={loadAll}
          onLogout={logout}
          loading={loading}
        />
      )}
    </div>
  );
}

// ---------- Login ----------
function LoginForm({ onDone }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await vapi.post('/vendor-portal/login', { email: email.trim().toLowerCase(), password });
      localStorage.setItem(V_TOKEN_KEY, r.data.session_token);
      // Persist workspace so subsequent requests are scoped correctly
      if (r.data.workspace) localStorage.setItem(V_WS_KEY, r.data.workspace);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-8">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong p-10 max-w-md w-full"
        data-testid="vendor-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center overflow-hidden p-2">
            <img src="/iris-icon.svg" alt="Iris" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-serif text-xl">Vendor Portal</div>
            <div className="overline text-[0.6rem]">Iris Realty</div>
          </div>
        </div>
        <h1 className="font-serif text-3xl mb-2">Sign in</h1>
        <p className="text-sm text-slate-600 mb-5">Use the credentials your Iris Realty admin shared with you.</p>
        <label className="block text-sm mb-3">Email
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid="vp-email"
                 className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
        </label>
        <label className="block text-sm mb-5">Password
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 data-testid="vp-password"
                 className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full text-sm" data-testid="vp-login-submit">
          {busy ? <Loader2 size={14} className="animate-spin inline"/> : 'Sign in'}
        </button>
        <div className="text-xs text-slate-500 mt-5 text-center">
          Trouble signing in? Contact your project manager at Iris Realty.
        </div>
      </motion.form>
    </div>
  );
}

// ---------- Force change password ----------
function ChangePasswordForm({ user, onDone }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Minimum 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      await vapi.post('/vendor-portal/change-password', { new_password: pw });
      toast.success('Password updated');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center p-8">
      <motion.form onSubmit={submit} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
                   className="glass-strong p-10 max-w-md w-full" data-testid="vendor-change-pw-form">
        <div className="overline mb-1 text-amber-800 flex items-center gap-2"><KeyRound size={12}/> Required</div>
        <h1 className="font-serif text-3xl mb-2">Set a new password</h1>
        <p className="text-sm text-slate-600 mb-5">Hi {user?.name}, this is your first login. Please choose a strong password (min 8 characters).</p>
        <label className="block text-sm mb-3">New password
          <input required type="password" value={pw} onChange={(e)=>setPw(e.target.value)}
                 data-testid="vp-new-pw"
                 className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
        </label>
        <label className="block text-sm mb-5">Confirm password
          <input required type="password" value={pw2} onChange={(e)=>setPw2(e.target.value)}
                 data-testid="vp-new-pw-confirm"
                 className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
        </label>
        <button disabled={busy} type="submit" className="btn-primary w-full text-sm" data-testid="vp-change-pw-submit">
          {busy ? 'Saving…' : 'Save & continue'}
        </button>
      </motion.form>
    </div>
  );
}

// ---------- Dashboard ----------
function VendorDashboard({ me, data, tab, setTab, onReload, onLogout, loading }) {
  const { invoices, boqs, snags, properties } = data;
  const perms = me?.user?.vendor_permissions || {};
  const canManageTeam = !!(me?.user?.is_vendor_admin || perms.manage_team);
  const paid = invoices.filter(i => i.status === 'paid').reduce((s,i) => s + (i.net_payable_aed || i.amount_aed || 0), 0);
  const pending = invoices.filter(i => i.status !== 'paid' && i.status !== 'rejected').reduce((s,i) => s + (i.amount_aed || 0), 0);
  const openSnags = snags.filter(s => s.status !== 'closed' && s.status !== 'resolved').length;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    perms.view_invoices !== false && { key: 'invoices', label: `Invoices (${invoices.length})` },
    perms.view_snags !== false && { key: 'snags', label: `Snags (${snags.length})` },
    perms.view_boqs !== false && { key: 'boqs', label: `BOQs (${boqs.length})` },
    { key: 'properties', label: `Properties (${properties.length})` },
    { key: 'team', label: canManageTeam ? 'Team' : 'Team (view)' },
  ].filter(Boolean);

  return (
    <div>
      <header className="px-8 py-5 flex items-center justify-between border-b border-white/40">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center overflow-hidden p-2">
            <img src="/iris-icon.svg" alt="Iris" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-serif text-xl">Iris Realty · Vendor Portal</div>
            <div className="overline text-[0.6rem]">{me?.vendor?.company || me?.user?.name}</div>
          </div>
        </div>
        <button onClick={onLogout} className="btn-ghost text-sm flex items-center gap-2" data-testid="vp-logout"><LogOut size={14}/> Logout</button>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-6 space-y-6">
        <div>
          <div className="overline">Welcome</div>
          <h1 className="font-serif text-4xl">Hi {me?.user?.name}</h1>
          <p className="text-slate-600 text-sm mt-1">{me?.vendor?.trade} · scoped to {properties.length} propert{properties.length === 1 ? 'y' : 'ies'}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass p-5"><div className="overline">Paid to you</div><div className="font-serif text-3xl mt-2 text-emerald-700">{AED_short(paid)}</div></div>
          <div className="glass p-5"><div className="overline">Awaiting payment</div><div className="font-serif text-3xl mt-2 text-amber-700">{AED_short(pending)}</div></div>
          <div className="glass p-5"><div className="overline">Open snags</div><div className="font-serif text-3xl mt-2 text-red-600">{openSnags}</div></div>
          <div className="glass p-5"><div className="overline">Active BOQs</div><div className="font-serif text-3xl mt-2">{boqs.length}</div></div>
        </div>

        <div className="glass p-1.5 inline-flex flex-wrap gap-1" data-testid="vp-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`vp-tab-${t.key}`}
                    className={`px-4 py-1.5 rounded-full text-sm ${tab === t.key ? 'bg-[#1A2530] text-white' : 'text-slate-600 hover:bg-white/60'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab data={data}/>}
        {tab === 'invoices' && <InvoicesTab invoices={invoices}/>}
        {tab === 'snags' && <SnagsTab snags={snags}/>}
        {tab === 'boqs' && <BOQsTab boqs={boqs}/>}
        {tab === 'properties' && <PropertiesTab properties={properties}/>}
        {tab === 'team' && <TeamTab me={me} vapi={vapi} canManage={canManageTeam}/>}
      </main>
    </div>
  );
}

function OverviewTab({ data }) {
  const recentInvs = data.invoices.slice(0, 5);
  const recentSnags = data.snags.filter(s => s.status !== 'closed' && s.status !== 'resolved').slice(0, 5);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="glass p-6">
        <div className="flex items-center gap-2 mb-4"><Receipt size={16}/><h3 className="font-serif text-xl">Recent invoices</h3></div>
        {recentInvs.length === 0 && <div className="text-sm text-slate-400">No invoices yet.</div>}
        {recentInvs.map(i => (
          <div key={i.id} className="flex justify-between py-2 border-b border-slate-100/70 text-sm">
            <span className="truncate">{i.invoice_number} · {i.description}</span>
            <span className="font-medium">{AED_short(i.amount_aed)}</span>
          </div>
        ))}
      </div>
      <div className="glass p-6">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle size={16} className="text-red-600"/><h3 className="font-serif text-xl">Open snags to fix</h3></div>
        {recentSnags.length === 0 && <div className="text-sm text-slate-400">Nothing pending — great job.</div>}
        {recentSnags.map(s => (
          <div key={s.id} className="py-2 border-b border-slate-100/70 text-sm">
            <div className="font-medium truncate">{s.title}</div>
            <div className="text-xs text-slate-500">{s.location} · {s.severity}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoicesTab({ invoices }) {
  return (
    <div className="glass p-6">
      <table className="w-full text-sm">
        <thead><tr className="overline text-left border-b border-slate-200/60"><th className="py-2">#</th><th>Description</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id} className="border-b border-slate-100/70" data-testid={`vp-inv-${inv.id}`}>
              <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
              <td className="max-w-xs truncate">{inv.description}</td>
              <td className="text-right">{AED(inv.amount_aed)}</td>
              <td><span className={`status-pill ${inv.status === 'paid' ? 'status-success' : inv.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>{inv.status?.replace(/_/g,' ')}</span></td>
            </tr>
          ))}
          {invoices.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">No invoices yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SnagsTab({ snags }) {
  return (
    <div className="space-y-2">
      {snags.length === 0 && <div className="glass p-12 text-center text-slate-400">No snags assigned to you.</div>}
      {snags.map(s => (
        <div key={s.id} className="glass p-4 flex items-center justify-between" data-testid={`vp-snag-${s.id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{s.title}</div>
            <div className="text-xs text-slate-500 mt-1">{s.location} · {s.description}</div>
          </div>
          <div className="text-right">
            <span className={`status-pill text-xs ${s.severity === 'critical' ? 'status-danger' : s.severity === 'major' ? 'status-warning' : 'status-gold'}`}>{s.severity}</span>
            <div className="text-xs text-slate-500 mt-1 capitalize">{s.status}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BOQsTab({ boqs }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {boqs.length === 0 && <div className="col-span-2 glass p-12 text-center text-slate-400">No BOQs assigned to you.</div>}
      {boqs.map(b => (
        <div key={b.id} className="glass p-5" data-testid={`vp-boq-${b.id}`}>
          <div className="flex items-center gap-2 mb-2"><FileSpreadsheet size={14}/><span className="text-sm font-medium">{b.title || b.id}</span></div>
          <div className="text-xs text-slate-500">{b.description}</div>
          <div className="mt-3 flex justify-between text-sm">
            <span className="text-slate-600">Total</span>
            <span className="font-medium">{AED_short(b.total_aed)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PropertiesTab({ properties }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {properties.length === 0 && <div className="col-span-3 glass p-12 text-center text-slate-400">No properties assigned to you.</div>}
      {properties.map(p => (
        <div key={p.id} className="glass p-4" data-testid={`vp-property-${p.id}`}>
          {p.thumbnail_url && <div className="h-32 rounded-xl overflow-hidden mb-3 bg-slate-200"><img src={p.thumbnail_url} alt="" className="w-full h-full object-cover"/></div>}
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Building2 size={11}/> {p.community}</div>
          <div className="font-medium text-sm">{p.title}</div>
          <div className="text-xs text-slate-500 mt-1 capitalize">Stage: {p.stage}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Team management (self-service) ----------
const PERM_META = [
  { key: 'view_boqs', label: 'View BOQs', hint: 'Approved quantities & unit rates' },
  { key: 'view_invoices', label: 'View Invoices', hint: 'Amounts owed and paid' },
  { key: 'view_snags', label: 'View Snags', hint: 'Site punch-list items' },
  { key: 'view_pos', label: 'View Purchase Orders', hint: 'Committed spend' },
  { key: 'view_files', label: 'View Files', hint: 'Attachments & docs' },
  { key: 'manage_team', label: 'Manage team', hint: 'Invite/edit/remove other team members' },
];

function TeamTab({ me, vapi, canManage }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [showTemp, setShowTemp] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await vapi.get('/vendor-portal/team');
      setTeam(r.data.team || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load team');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onInvited = (res) => {
    setShowTemp({ email: res.user.email, temp_password: res.temp_password, existed: res.existed });
    setInviting(false);
    load();
  };

  return (
    <div className="glass p-6" data-testid="vp-team-tab">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#679286]"/>
            <h3 className="font-serif text-2xl">Your team</h3>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {canManage
              ? "Invite your accounts, contracts and site teams. Fine-tune what each person can see."
              : "You can see the team roster. Only vendor admins can invite or edit."}
          </div>
        </div>
        {canManage && (
          <button onClick={() => setInviting(true)} data-testid="vp-team-invite-btn"
            className="btn-primary text-sm inline-flex items-center gap-2">
            <UserPlus size={14}/> Invite team member
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin"/> Loading team…
        </div>
      ) : team.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No team members yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left py-2.5 px-3">Name</th>
                <th className="text-left py-2.5 px-3">Email</th>
                <th className="text-left py-2.5 px-3">Access</th>
                <th className="text-left py-2.5 px-3">Status</th>
                {canManage && <th className="text-right py-2.5 px-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {team.map(u => {
                const isSelf = u.user_id === me?.user?.user_id;
                const permsCount = Object.values(u.vendor_permissions || {}).filter(Boolean).length;
                return (
                  <tr key={u.user_id} className="border-t border-slate-100" data-testid={`vp-team-row-${u.user_id}`}>
                    <td className="py-3 px-3">
                      <div className="font-medium text-slate-900">
                        {u.name}
                        {isSelf && <span className="ml-1.5 text-[10px] text-slate-400">(you)</span>}
                        {u.is_vendor_admin && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-widest">
                            <ShieldCheck size={9}/> Admin
                          </span>
                        )}
                      </div>
                      {u.job_title && <div className="text-xs text-slate-500">{u.job_title}</div>}
                    </td>
                    <td className="py-3 px-3 text-slate-700 text-xs">{u.email}</td>
                    <td className="py-3 px-3 text-xs text-slate-600">
                      {u.is_vendor_admin ? 'Full access' : `${permsCount} of ${PERM_META.length} permissions`}
                    </td>
                    <td className="py-3 px-3">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400"/> Deactivated
                        </span>
                      )}
                      {u.must_change_password && (
                        <div className="text-[10px] text-amber-600 mt-0.5">Pending first login</div>
                      )}
                    </td>
                    {canManage && (
                      <td className="py-3 px-3 text-right">
                        <button onClick={() => setEditingUser(u)} data-testid={`vp-team-edit-${u.user_id}`}
                          className="text-xs text-[#679286] hover:text-[#4a6d63] font-medium">Manage</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviting && canManage && (
        <TeamInviteModal onClose={() => setInviting(false)} onDone={onInvited} vapi={vapi} adminAssignedProps={me?.user?.assigned_property_ids || []}/>
      )}
      {showTemp && <TempPasswordModal data={showTemp} onClose={() => setShowTemp(null)}/>}
      {editingUser && (
        <TeamEditModal user={editingUser} me={me} vapi={vapi}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { setEditingUser(null); await load(); }}
          onPasswordReset={(data) => { setEditingUser(null); setShowTemp(data); load(); }}/>
      )}
    </div>
  );
}

function TeamInviteModal({ onClose, onDone, vapi, adminAssignedProps }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [perms, setPerms] = useState({
    view_boqs: true, view_invoices: true, view_snags: true,
    view_pos: true, view_files: true, manage_team: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await vapi.post('/vendor-portal/team', {
        email, name, job_title: jobTitle,
        vendor_permissions: perms,
        assigned_property_ids: adminAssignedProps,
      });
      onDone(r.data);
      toast.success(r.data.existed ? 'Team member updated' : 'Invite created — copy the temp password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="vp-invite-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6d63] grid place-items-center"><UserPlus size={20} className="text-white"/></div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl">Invite team member</h2>
            <div className="text-xs text-slate-500">They&apos;ll get a one-time temp password.</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">Full name *
              <input required value={name} onChange={e=>setName(e.target.value)} data-testid="vp-invite-name"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
            </label>
            <label className="block text-sm">Job title
              <input value={jobTitle} onChange={e=>setJobTitle(e.target.value)} placeholder="e.g. Site Foreman" data-testid="vp-invite-job"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
            </label>
          </div>
          <label className="block text-sm">Email address *
            <input required type="email" value={email} onChange={e=>setEmail(e.target.value)} data-testid="vp-invite-email"
              className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
          </label>
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Permissions</div>
            <div className="grid grid-cols-1 gap-1.5 rounded-2xl border border-slate-200 bg-slate-50/40 p-3">
              {PERM_META.map(p => (
                <label key={p.key} className="flex items-start gap-2.5 py-1 cursor-pointer hover:bg-white/60 rounded-lg px-2 -mx-2">
                  <input type="checkbox" checked={!!perms[p.key]}
                    onChange={e => setPerms(prev => ({ ...prev, [p.key]: e.target.checked }))}
                    data-testid={`vp-invite-perm-${p.key}`}
                    className="mt-0.5 w-4 h-4 accent-[#679286]"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                      {p.label}
                      {p.key === 'manage_team' && <ShieldCheck size={11} className="text-emerald-600"/>}
                    </div>
                    <div className="text-[10px] text-slate-500">{p.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/60">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm hover:bg-white">Cancel</button>
          <button type="submit" disabled={busy || !email || !name} data-testid="vp-invite-submit"
            className="px-5 py-2 rounded-full bg-[#679286] text-white text-sm font-semibold hover:bg-[#4a6d63] transition disabled:opacity-40 inline-flex items-center gap-2">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Inviting…</> : <><UserPlus size={14}/> Send invite</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function TeamEditModal({ user, me, vapi, onClose, onSaved, onPasswordReset }) {
  const isSelf = user.user_id === me?.user?.user_id;
  const [name, setName] = useState(user.name || '');
  const [jobTitle, setJobTitle] = useState(user.job_title || '');
  const [perms, setPerms] = useState({
    view_boqs: true, view_invoices: true, view_snags: true,
    view_pos: true, view_files: true, manage_team: false,
    ...(user.vendor_permissions || {}),
  });
  const [isActive, setIsActive] = useState(user.is_active !== false);
  const [busy, setBusy] = useState(false);
  const [busyPw, setBusyPw] = useState(false);
  const [busyDel, setBusyDel] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await vapi.patch(`/vendor-portal/team/${user.user_id}`, {
        name, job_title: jobTitle,
        vendor_permissions: perms, is_active: isActive,
      });
      toast.success('Team member updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset password for ${user.name}? Their current sessions will be terminated.`)) return;
    setBusyPw(true);
    try {
      const r = await vapi.post(`/vendor-portal/team/${user.user_id}/reset-password`);
      onPasswordReset({ email: user.email, temp_password: r.data.temp_password, existed: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setBusyPw(false); }
  };

  const removeUser = async () => {
    if (!window.confirm(`Remove ${user.name} from the team? They will lose access immediately.`)) return;
    setBusyDel(true);
    try {
      await vapi.delete(`/vendor-portal/team/${user.user_id}`);
      toast.success(`${user.name} removed from the team`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally { setBusyDel(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="vp-edit-modal">
      <form onSubmit={save} onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#679286] to-[#4a6d63] grid place-items-center"><Shield size={20} className="text-white"/></div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl">Manage {user.name}</h2>
            <div className="text-xs text-slate-500">{user.email}{isSelf && ' · (you)'}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center"><X size={18}/></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">Name
              <input value={name} onChange={e=>setName(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
            </label>
            <label className="block text-sm">Job title
              <input value={jobTitle} onChange={e=>setJobTitle(e.target.value)} placeholder="e.g. Accounts Manager"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"/>
            </label>
          </div>
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Permissions</div>
            <div className="grid grid-cols-1 gap-1.5 rounded-2xl border border-slate-200 bg-slate-50/40 p-3">
              {PERM_META.map(p => (
                <label key={p.key} className="flex items-start gap-2.5 py-1 cursor-pointer hover:bg-white/60 rounded-lg px-2 -mx-2">
                  <input type="checkbox" checked={!!perms[p.key]}
                    onChange={e => setPerms(prev => ({ ...prev, [p.key]: e.target.checked }))}
                    data-testid={`vp-edit-perm-${p.key}`}
                    disabled={isSelf && p.key === 'manage_team'}
                    className="mt-0.5 w-4 h-4 accent-[#679286]"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                      {p.label}
                      {p.key === 'manage_team' && <ShieldCheck size={11} className="text-emerald-600"/>}
                    </div>
                    <div className="text-[10px] text-slate-500">{p.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {!isSelf && (
            <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
              <input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)}
                className="w-4 h-4 accent-[#679286]" data-testid="vp-edit-active"/>
              Account active (uncheck to suspend without deleting)
            </label>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 bg-slate-50/60">
          <div className="flex items-center gap-2">
            <button type="button" onClick={resetPassword} disabled={busyPw} data-testid="vp-edit-reset-pw"
              className="px-3 py-2 rounded-full border border-slate-200 text-xs hover:bg-white inline-flex items-center gap-1.5">
              {busyPw ? <Loader2 size={12} className="animate-spin"/> : <RotateCw size={12}/>} Reset password
            </button>
            {!isSelf && (
              <button type="button" onClick={removeUser} disabled={busyDel} data-testid="vp-edit-remove"
                className="px-3 py-2 rounded-full border border-red-200 text-red-600 text-xs hover:bg-red-50 inline-flex items-center gap-1.5">
                {busyDel ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Remove
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm hover:bg-white">Cancel</button>
            <button type="submit" disabled={busy} data-testid="vp-edit-save"
              className="px-5 py-2 rounded-full bg-[#679286] text-white text-sm font-semibold hover:bg-[#4a6d63] transition disabled:opacity-40 inline-flex items-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Check size={14}/> Save changes</>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function TempPasswordModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.temp_password);
      setCopied(true); toast.success('Copied to clipboard');
      setTimeout(()=>setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="vp-temp-pw-modal">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-3">
            <KeyRound size={22} className="text-emerald-600"/>
          </div>
          <h3 className="font-serif text-2xl">Temp password ready</h3>
          <div className="text-sm text-slate-500 mt-1">Share with <span className="font-medium">{data.email}</span> over WhatsApp or email.<br/>They&apos;ll be forced to change it on first login.</div>
          <div className="mt-4 p-4 rounded-2xl bg-slate-900 text-white font-mono text-lg tracking-wider select-all" data-testid="vp-temp-pw-value">
            {data.temp_password}
          </div>
          <button onClick={copy} data-testid="vp-temp-pw-copy"
            className="mt-3 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm hover:bg-slate-50 inline-flex items-center gap-2">
            {copied ? <><Check size={14} className="text-emerald-600"/> Copied</> : <><Copy size={14}/> Copy password</>}
          </button>
          <div className="mt-5">
            <button onClick={onClose} className="btn-primary text-sm">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
